import React from 'react'
import { MIME_TYPE } from 'src/Utils/globalEnums'
import { useGooglePicker } from 'src/lib/Linkers/modals/Google Drive Wizard/GoogleDriveWizard.hooks'
import { useGoogleDriveContext } from 'src/lib/Linkers/context/GoogleDriveContext'
import { FileSelectorText } from 'src/components/Reusable/Forms/FileSelector/FileSelector.styles'
import { useSetActiveModal } from 'src/Modal/ModalStateContext'
import { ModalFormId } from 'src/components/ModalForms'
import { useSharepointFilePickerContext } from 'src/lib/Linkers/modals/SharePoint Picker/SharepointFilePickerContext'
import { ExtendedDocumentObject } from 'src/lib/Linkers/Linkers.constants'
import { FlexRow, FlexAlign } from 'src/components/Reusable/Flex'
import { useSharepointLinkPaste } from 'src/context/Gatekeeper'
import { useConfluenceFilePickerContext } from 'src/lib/Linkers/modals/Confluence Picker/ConfluenceFilePickerContext'
import { ReactComponent as LinkIcon } from '../../../components/Icons/assets/permalink-icon.svg'
import { TypeButton } from '../FileType/TypeSelector.styles'
import { DocumentFormData, DocumentType } from './AddDocumentForm'
import {
  DMTitle,
  DMSize,
  GoogleDriveIcon,
  GoogleDriveSmallIcon,
  SharePointIcon,
  SharePointSmallIcon,
  DocumentManagementFilesContainer,
  ConfluenceIcon,
} from './AddDocumentForm.styles'

export const PolicyDocLinkTypeButton = ({
  setFormData,
  setType,
}: {
  setFormData: React.Dispatch<React.SetStateAction<DocumentFormData>>
  setType: React.Dispatch<React.SetStateAction<DocumentType>>
}): JSX.Element => {
  const onClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setFormData((state) => ({ ...state, mime: MIME_TYPE.TEXT_URI_LIST }))
    setType(DocumentType.LINK)
  }

  return (
    <TypeButton onClick={onClick}>
      <LinkIcon width="48px" height="48px" />
      <FileSelectorText>Paste Link</FileSelectorText>
    </TypeButton>
  )
}

export const PolicyDocGoogleDriveButton = ({
  policyId,
  hide,
  setGdriveError,
}: {
  policyId: string
  hide: () => void
  setGdriveError: React.Dispatch<React.SetStateAction<string>>
}): JSX.Element => {
  const openPicker = useGooglePicker()
  const { setPickerConfig, setSubjectIds, setSource } = useGoogleDriveContext()

  return (
    <TypeButton
      onClick={async (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setSubjectIds([policyId])
        setSource('Policy')
        setPickerConfig({
          allowMultiselect: true,
          viewId: google.picker.ViewId.DOCS,
          modalId: ModalFormId.ADD_DOCUMENT,
        })
        const config = {
          allowMultiselect: true,
          viewId: google.picker.ViewId.DOCS,
          modalId: ModalFormId.ADD_DOCUMENT,
        }
        const { isShown, invalidCredentials, exceptions } = await openPicker(
          config,
        )
        if (isShown) {
          hide()
        } else if (exceptions) {
          setGdriveError(exceptions)
          setPickerConfig(null)
        } else if (invalidCredentials) {
          setGdriveError('Invalid Credentials.')
          setPickerConfig(null)
        }
      }}
    >
      <GoogleDriveIcon />
      <FileSelectorText>Google Drive</FileSelectorText>
    </TypeButton>
  )
}

export const PolicyDocSharepointButton = ({
  setFormData,
  setType,
}: {
  setFormData: React.Dispatch<React.SetStateAction<DocumentFormData>>
  setType: React.Dispatch<React.SetStateAction<DocumentType>>
}): JSX.Element => {
  const openModal = useSetActiveModal(ModalFormId.SHAREPOINT_FILE_PICKER)
  const useSPLinkPaste = useSharepointLinkPaste()

  return (
    <TypeButton
      onClick={(e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (useSPLinkPaste) {
          setFormData((state) => ({ ...state, mime: MIME_TYPE.TEXT_URI_LIST }))
          setType(DocumentType.SHAREPOINTLINK)
        } else {
          openModal()
        }
      }}
    >
      <SharePointIcon />
      <FileSelectorText>Sharepoint</FileSelectorText>
    </TypeButton>
  )
}

export const PolicyDocConfluenceButton = ({
  policyId,
  setFormData,
  setType,
}: {
  policyId: string
  setFormData?: React.Dispatch<React.SetStateAction<DocumentFormData>>
  setType?: React.Dispatch<React.SetStateAction<DocumentType>>
}): JSX.Element => {
  const openModal = useSetActiveModal(ModalFormId.CONFLUENCE_FILE_PICKER)
  const { setSubjectIds, setSource } = useConfluenceFilePickerContext()

  return (
    <TypeButton
      onClick={(e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()

        // Align with Google Drive pattern: configure context before opening modal
        setSubjectIds([policyId])
        setSource('Policy')

        // We currently don't need to touch formData/type for Confluence,
        // since linking is handled entirely via the linker context.
        // setFormData and setType are accepted for API symmetry but unused.
        void setFormData
        void setType

        openModal()
      }}
    >
      <ConfluenceIcon />
      <FileSelectorText>Confluence</FileSelectorText>
    </TypeButton>
  )
}

export const PolicyDocumentManagementFiles = (): JSX.Element => {
  const { filesSelected: spFileSelected } = useSharepointFilePickerContext()
  const { filesSelected: gdFileSelected } = useGoogleDriveContext()
  const { filesSelected: cfFileSelected } = useConfluenceFilePickerContext()

  const spFiles = spFileSelected ?? []
  const gdFiles: ExtendedDocumentObject[] = gdFileSelected ?? []
  const cfFiles = cfFileSelected ?? []

  if (!spFiles.length && !gdFiles.length && !cfFiles.length) {
    return <></>
  }

  return (
    <DocumentManagementFilesContainer>
      {spFiles.map((file) => (
        <FlexRow
          key={file.id}
          justify={FlexAlign.FLEX_START}
          align={FlexAlign.CENTER}
          $padding={12}
          gap={8}
          isFullWidth
        >
          <SharePointSmallIcon />
          <DMTitle>{file.name}</DMTitle>
          {file.size && <DMSize>{file.size} bytes</DMSize>}
        </FlexRow>
      ))}
      {gdFiles.map((file) => (
        <FlexRow
          key={file.id}
          justify={FlexAlign.FLEX_START}
          align={FlexAlign.CENTER}
          $padding={12}
          gap={8}
          isFullWidth
        >
          <GoogleDriveSmallIcon />
          <DMTitle>{file.name}</DMTitle>
          {file.sizeBytes && <DMSize>{file.sizeBytes} bytes</DMSize>}
        </FlexRow>
      ))}
      {cfFiles.map((node) => (
        <FlexRow
          key={node.id}
          justify={FlexAlign.FLEX_START}
          align={FlexAlign.CENTER}
          $padding={12}
          gap={8}
          isFullWidth
        >
          <ConfluenceIcon />
          <DMTitle>{node.title ?? node.fileName ?? node.id}</DMTitle>
        </FlexRow>
      ))}
    </DocumentManagementFilesContainer>
  )
}
